// UX Utilities - Toast Notifications, Loading States, Smooth Animations

// Toast Notification System
class ToastManager {
  constructor() {
    this.container = null;
    this.init();
  }

  init() {
    // Create toast container if it doesn't exist
    if (!document.querySelector('.toast-container')) {
      this.container = document.createElement('div');
      this.container.className = 'toast-container';
      document.body.appendChild(this.container);
    } else {
      this.container = document.querySelector('.toast-container');
    }
  }

  show(message, type = 'info', duration = 5000) {
    const toast = document.createElement('div');
    toast.className = `toast ${type} toast-enter`;
    
    const icon = this.getIcon(type);
    
    toast.innerHTML = `
      <div class="flex-shrink-0">
        ${icon}
      </div>
      <div class="flex-1">
        <p class="font-medium">${message}</p>
      </div>
      <button class="flex-shrink-0 ml-2 text-current opacity-70 hover:opacity-100 transition-opacity" onclick="this.parentElement.remove()">
        <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
        </svg>
      </button>
    `;

    this.container.appendChild(toast);

    // Trigger animation
    setTimeout(() => {
      toast.classList.remove('toast-enter');
    }, 10);

    // Auto remove
    if (duration > 0) {
      setTimeout(() => {
        this.remove(toast);
      }, duration);
    }

    return toast;
  }

  remove(toast) {
    toast.classList.add('toast-exit');
    setTimeout(() => {
      toast.remove();
    }, 300);
  }

  getIcon(type) {
    const icons = {
      success: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"/>
      </svg>`,
      error: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
      </svg>`,
      warning: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
      </svg>`,
      info: `<svg class="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
        <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clip-rule="evenodd"/>
      </svg>`
    };
    return icons[type] || icons.info;
  }
}

// Create global toast instance
const toast = new ToastManager();

// Loading State Manager
class LoadingManager {
  static show(element, text = 'Loading...') {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    
    if (!element) return;

    element.dataset.originalContent = element.innerHTML;
    element.disabled = true;
    element.style.position = 'relative';
    
    element.innerHTML = `
      <span class="flex items-center justify-center gap-2">
        <svg class="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <span>${text}</span>
      </span>
    `;
  }

  static hide(element) {
    if (typeof element === 'string') {
      element = document.querySelector(element);
    }
    
    if (!element) return;

    element.disabled = false;
    if (element.dataset.originalContent) {
      element.innerHTML = element.dataset.originalContent;
      delete element.dataset.originalContent;
    }
  }
}

// Scroll Progress Bar
class ScrollProgress {
  constructor() {
    this.progressBar = null;
    this.init();
  }

  init() {
    // Create progress bar
    this.progressBar = document.createElement('div');
    this.progressBar.className = 'scroll-progress';
    this.progressBar.style.transform = 'scaleX(0)';
    document.body.appendChild(this.progressBar);

    // Update on scroll
    window.addEventListener('scroll', () => this.update());
    this.update();
  }

  update() {
    const windowHeight = window.innerHeight;
    const documentHeight = document.documentElement.scrollHeight;
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollPercent = scrollTop / (documentHeight - windowHeight);
    
    this.progressBar.style.transform = `scaleX(${scrollPercent})`;
  }
}

// Smooth Reveal on Scroll
class ScrollReveal {
  constructor(selector = '.fade-in-up, .slide-in-left, .slide-in-right', options = {}) {
    this.elements = document.querySelectorAll(selector);
    this.options = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px',
      ...options
    };
    this.init();
  }

  init() {
    if (!('IntersectionObserver' in window)) {
      // Fallback for browsers without IntersectionObserver
      this.elements.forEach(el => {
        el.style.opacity = '1';
        el.style.transform = 'none';
      });
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const target = entry.target;
          
          // Handle different animation types
          if (target.classList.contains('slide-in-left') || target.classList.contains('slide-in-right')) {
            target.classList.add('visible');
          } else if (target.classList.contains('fade-in-up')) {
            target.style.opacity = '1';
            target.style.animation = 'fadeInUp 0.6s ease-out forwards';
          }
          
          observer.unobserve(target);
        }
      });
    }, this.options);

    this.elements.forEach(el => {
      // Add observe class and set initial state for slide-in animations
      if (el.classList.contains('slide-in-left') || el.classList.contains('slide-in-right')) {
        el.classList.add('observe');
      } else if (el.classList.contains('fade-in-up')) {
        el.style.opacity = '0';
      }
      observer.observe(el);
    });
  }
}

// Form Validation Helper
class FormValidator {
  static validate(formElement) {
    const inputs = formElement.querySelectorAll('input[required], select[required], textarea[required]');
    let isValid = true;
    const errors = [];

    inputs.forEach(input => {
      const error = this.validateInput(input);
      if (error) {
        isValid = false;
        errors.push({ element: input, message: error });
        this.showError(input, error);
      } else {
        this.clearError(input);
      }
    });

    return { isValid, errors };
  }

  static validateInput(input) {
    const value = input.value.trim();
    const type = input.type;
    const name = input.name || input.id;

    if (input.hasAttribute('required') && !value) {
      return `${this.getFieldName(name)} is required`;
    }

    if (type === 'email' && value) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(value)) {
        return 'Please enter a valid email address';
      }
    }

    if (type === 'password' && value) {
      if (value.length < 8) {
        return 'Password must be at least 8 characters';
      }
    }

    if (input.hasAttribute('min') && type === 'number') {
      const min = parseFloat(input.getAttribute('min'));
      if (parseFloat(value) < min) {
        return `Value must be at least ${min}`;
      }
    }

    if (input.hasAttribute('max') && type === 'number') {
      const max = parseFloat(input.getAttribute('max'));
      if (parseFloat(value) > max) {
        return `Value must be no more than ${max}`;
      }
    }

    return null;
  }

  static showError(input, message) {
    input.classList.add('input-error');
    
    // Remove existing error message
    const existingError = input.parentElement.querySelector('.error-message');
    if (existingError) {
      existingError.remove();
    }

    // Add new error message
    const errorDiv = document.createElement('div');
    errorDiv.className = 'error-message text-error-500 text-sm mt-1';
    errorDiv.textContent = message;
    input.parentElement.appendChild(errorDiv);
  }

  static clearError(input) {
    input.classList.remove('input-error');
    const errorDiv = input.parentElement.querySelector('.error-message');
    if (errorDiv) {
      errorDiv.remove();
    }
  }

  static getFieldName(name) {
    return name
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase())
      .trim();
  }
}

// Debounce Utility
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Throttle Utility
function throttle(func, limit) {
  let inThrottle;
  return function(...args) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => inThrottle = false, limit);
    }
  };
}

// Copy to Clipboard
async function copyToClipboard(text) {
  try {
    await navigator.clipboard.writeText(text);
    toast.show('Copied to clipboard!', 'success', 2000);
    return true;
  } catch (err) {
    toast.show('Failed to copy', 'error', 2000);
    return false;
  }
}

// Format Number with Animation
function animateValue(element, start, end, duration = 1000) {
  const range = end - start;
  const increment = range / (duration / 16);
  let current = start;
  
  const timer = setInterval(() => {
    current += increment;
    if ((increment > 0 && current >= end) || (increment < 0 && current <= end)) {
      current = end;
      clearInterval(timer);
    }
    element.textContent = Math.round(current).toLocaleString();
  }, 16);
}

// Initialize all UX features
function initUX() {
  // Initialize scroll progress
  new ScrollProgress();
  
  // Initialize scroll reveal for all animation classes
  new ScrollReveal('.fade-in-up, .slide-in-left, .slide-in-right');
  
  // Initialize back to top button
  initBackToTop();
  
  // Add smooth scroll to anchor links
  document.querySelectorAll('a[href^="#"]').forEach(anchor => {
    anchor.addEventListener('click', function (e) {
      const href = this.getAttribute('href');
      if (href !== '#' && href !== '') {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) {
          target.scrollIntoView({
            behavior: 'smooth',
            block: 'start'
          });
        }
      }
    });
  });

  // Add ripple effect to buttons
  document.querySelectorAll('.btn').forEach(button => {
    button.addEventListener('click', function(e) {
      const ripple = document.createElement('span');
      const rect = this.getBoundingClientRect();
      const size = Math.max(rect.width, rect.height);
      const x = e.clientX - rect.left - size / 2;
      const y = e.clientY - rect.top - size / 2;
      
      ripple.style.width = ripple.style.height = size + 'px';
      ripple.style.left = x + 'px';
      ripple.style.top = y + 'px';
      ripple.classList.add('ripple');
      
      this.appendChild(ripple);
      
      setTimeout(() => ripple.remove(), 600);
    });
  });

  // Add loading state to forms
  document.querySelectorAll('form').forEach(form => {
    form.addEventListener('submit', function(e) {
      const submitBtn = this.querySelector('button[type="submit"]');
      if (submitBtn && !submitBtn.disabled) {
        // Validation will be handled by the form's own logic
        // This just adds visual feedback
      }
    });
  });

  // Add hover effects to cards
  enhanceCards();
  
  // Add keyboard navigation improvements
  enhanceKeyboardNav();
}

// Back to Top Button
function initBackToTop() {
  const backToTopBtn = document.getElementById('backToTop');
  if (!backToTopBtn) return;

  window.addEventListener('scroll', throttle(() => {
    if (window.pageYOffset > 300) {
      backToTopBtn.style.opacity = '1';
      backToTopBtn.style.pointerEvents = 'auto';
    } else {
      backToTopBtn.style.opacity = '0';
      backToTopBtn.style.pointerEvents = 'none';
    }
  }, 100));

  backToTopBtn.addEventListener('click', () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  });
}

// Enhance card interactions
function enhanceCards() {
  document.querySelectorAll('.card-hover').forEach(card => {
    card.addEventListener('mouseenter', function() {
      this.style.transform = 'translateY(-4px) scale(1.02)';
    });
    
    card.addEventListener('mouseleave', function() {
      this.style.transform = 'translateY(0) scale(1)';
    });
  });
}

// Enhance keyboard navigation
function enhanceKeyboardNav() {
  // Add visible focus indicators
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Tab') {
      document.body.classList.add('keyboard-nav');
    }
  });
  
  document.addEventListener('mousedown', () => {
    document.body.classList.remove('keyboard-nav');
  });
}

// Export utilities
export {
  toast,
  ToastManager,
  LoadingManager,
  ScrollProgress,
  ScrollReveal,
  FormValidator,
  debounce,
  throttle,
  copyToClipboard,
  animateValue,
  initUX
};
